import React, { useEffect, useState } from 'react';
import axios from 'axios';
import login from '../assets/login.svg';
import LogoLight from '../assets/logo1.png';
import LogoDark from '../assets/logo2.png';
import { tmAIAssistantDefaultValue } from '../constant';
import { useNavigate } from 'react-router-dom';
import FloatingButton from '../components/FloatingButton';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/navbar';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const { theme, setTheme } = useTheme();
  const [logoSrc, setLogoSrc] = useState(theme === 'light' ? LogoLight : LogoDark);

  const navigate = useNavigate();

  const applyThemeChoice = (nextTheme) => {
    document.documentElement.setAttribute('data-theme', nextTheme);
    document.body?.setAttribute('data-theme', nextTheme);
    localStorage.setItem('themeMode', 'manual');
    localStorage.setItem('theme', nextTheme);
    setTheme(nextTheme);
  };

  useEffect(() => {
const handleShortcut = (e) => {
  // ✅ Ignore if user is typing in a form field
  const tag = document.activeElement?.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

  const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';
  if (key !== 'i' && e.code !== 'KeyI') return;
  e.preventDefault();
  applyThemeChoice(theme === 'dark' ? 'light' : 'dark');
};
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [theme]);

  const speakPageContent = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;

    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
      return;
    }

    synth.cancel();

    // Function to select female voice
  const selectFemaleVoice = (voiceList) => {
    // Comprehensive list of female voice names across all browsers
    const allFemaleVoiceNames = [
      // Premium UK female voices
      'victoria', 'karen', 'moira', 'samantha', 'fiona', 'susan',
      // Alternative UK female voices
      'zoe', 'serena', 'hazel', 'lucy', 'anna', 'ellen', 'emily',
      // US female voices
      'zira', 'cortana', 'julia', 'aria',
      // Google/Chrome female voices
      'Google UK English Female', 'google us english female',
      // Additional known female names
      'siri', 'alexa', 'female', 'woman', 'lady', 'girl',
      'ava', 'bella', 'clara', 'diana', 'eva', 'grace', 'ivy',
      'kate', 'lily', 'mia', 'olivia', 'rose', 'sophia', 'lily'
    ];

    // Priority 1: Find premium UK English voices (including Google)
    const ukPremiumFemaleNames = ['victoria', 'karen', 'moira', 'samantha', 'fiona', 'susan', 'Google UK English Female'];
    for (const name of ukPremiumFemaleNames) {
      const voice = voiceList.find(
        v => v.lang === 'en-GB' && v.name.toLowerCase() === name
      );
      if (voice) return voice;
    }

    // Priority 2: Find any UK female voice (en-GB)
    const ukVoices = voiceList.filter(v => v.lang === 'en-GB');
    for (const voice of ukVoices) {
      const nameLower = voice.name.toLowerCase();
      if (allFemaleVoiceNames.some(name => nameLower.includes(name))) {
        return voice;
      }
    }

    // Priority 3: Find US female voices (en-US)
    const usVoices = voiceList.filter(v => v.lang.startsWith('en-US'));
    for (const voice of usVoices) {
      const nameLower = voice.name.toLowerCase();
      if (allFemaleVoiceNames.some(name => nameLower.includes(name))) {
        return voice;
      }
    }

    // Priority 4: Find any English female voice
    const englishVoices = voiceList.filter(v => v.lang.startsWith('en'));
    for (const voice of englishVoices) {
      const nameLower = voice.name.toLowerCase();
      if (allFemaleVoiceNames.some(name => nameLower.includes(name))) {
        return voice;
      }
    }

    // Priority 5: If no explicit female match found, try to find voice with 'female' keyword
    const genericFemaleVoice = voiceList.find(
      v => v.name.toLowerCase().includes('female') && v.lang.startsWith('en')
    );
    if (genericFemaleVoice) return genericFemaleVoice;

    // Priority 6: Return first non-male English voice (fallback strategy)
    const maleKeywords = ['male', 'man', 'boy', 'george', 'david', 'john', 'james', 'robert', 'michael', 'daniel'];
    const nonMaleVoice = voiceList.find(
      v => v.lang.startsWith('en') && !maleKeywords.some(keyword => v.name.toLowerCase().includes(keyword))
    );
    if (nonMaleVoice) return nonMaleVoice;

    // Final fallback: return any English voice (when no voice data is available)
    return voiceList.find(v => v.lang.startsWith('en'));
  };

    const setVoiceAndSpeak = () => {
      const voices = synth.getVoices();
      const selectedVoice = selectFemaleVoice(voices);

      const utterance = new SpeechSynthesisUtterance(document.body.innerText);
      utterance.onend = () => setIsSpeaking(false);

      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang;
      } else {
        utterance.lang = 'en-GB';
      }

      synth.speak(utterance);
      setIsSpeaking(true);
    };

    const voices = synth.getVoices();
    if (voices.length === 0) {
      const handleVoicesChanged = () => {
        setVoiceAndSpeak();
        synth.removeEventListener('voiceschanged', handleVoicesChanged);
      };
      synth.addEventListener('voiceschanged', handleVoicesChanged);
      return; // Don't speak yet, wait for voices
    }

    setVoiceAndSpeak();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}auth/login`,
        { email, password }
      );

      const { access_token, role, active_interview, name } = response.data;

      localStorage.setItem('authToken', access_token);
      localStorage.setItem('role', role?.toLowerCase().trim() || '');  // Normalize role to lowercase
      localStorage.setItem('name', name);
      localStorage.setItem('active_interview', active_interview);
      localStorage.setItem(
        'rnibAutoplayAfterLogin',
        role !== 'admin' && localStorage.getItem('rnibAudioEnabled') === 'true' ? 'true' : 'false'
      );

      if (role === 'admin') {
        navigate('/admin-dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  /* Theme Observer for Logo */
  useEffect(() => {
    setLogoSrc(theme === 'light' ? LogoLight : LogoDark);
  }, [theme]);

  return (
    <>
      <Navbar />
      <main className="flex min-h-[calc(100vh-90px)] items-center justify-center bg-panel px-3 py-6 text-text-light sm:px-6 lg:px-8">

        <div className="grid w-full max-w-5xl grid-cols-1 overflow-hidden rounded-xl border border-glass-border bg-panel shadow-lg md:grid-cols-2">

          {/* Left Section - Desktop */}
          <div className="hidden flex-col items-center justify-center bg-panel p-7 md:flex lg:p-10">
            <img
              src={login}
              alt=""
              aria-hidden="true"
              className="w-72 max-w-full"
            />
          </div>

          {/* Mobile Section - Centered */}
          <div className="flex flex-col items-center justify-center bg-panel p-6 md:hidden">
            <img
              src={login}
              alt=""
              aria-hidden="true"
              className="w-48 max-w-full"
            />
          </div>

          {/* Right Section - Form */}
          <section className="flex items-center justify-center bg-panel p-5 sm:p-8 md:col-span-1 md:p-10">
          <div className="w-full max-w-md text-left">

            <header className="mb-8 sm:mb-10">
              <div className="heading-banner block sm:inline-block w-full sm:w-auto text-center sm:text-left">
                <h1 className="text-[24px] font-bold text-center sm:text-left text-white">
                  Welcome
                </h1>
              </div>

              <p className="text-base text-text-light mt-4">
                Please enter your email and password to continue.
              </p>
            </header>

            {error && (
              <div
                role="alert"
                className="border border-red-500 bg-red-100 text-red-800 p-3 rounded-md mb-6 text-sm font-semibold"
              >
                Error: {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-base font-semibold text-text-light mb-2"
                >
                  Email Address
                </label>

                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-2 py-3 border border-glass-border rounded-md text-base bg-bg text-text-light placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-base font-semibold text-text-light mb-2"
                >
                  Password
                </label>

                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-2 py-3 border border-glass-border rounded-md text-base bg-bg text-text-light placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-accent text-[color:var(--accent-foreground)] py-3 rounded-md text-base font-bold hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
              >
                {loading ? 'Logging in…' : 'Login'}
              </button>

            </form>

          </div>
        </section>

      </div>

      <FloatingButton
        screen=""
        isSpeaking={isSpeaking}
        onSpeak={speakPageContent}
      />
    </main>
    </>
  );
}
